import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatCurrency, formatPercent, cn } from '@/lib/utils';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  Activity,
  Zap,
  Clock,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const [revenueData, setRevenueData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const [revenueRes, assetsRes] = await Promise.all([
        axios.get(`${API}/dashboard/revenue`),
        axios.get(`${API}/assets`),
      ]);
      setRevenueData(revenueRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  // Prepare waterfall data
  const waterfallData = revenueData?.waterfall || [];

  // Prepare scatter data for performance vs revenue
  const scatterData = assets.slice(0, 20).map((asset) => ({
    name: asset.asset_id,
    performance: asset.performance_ratio,
    revenue: asset.revenue_mtd,
    availability: asset.availability,
    status: asset.status,
  }));

  // Top underperforming assets
  const underperformingAssets = [...assets]
    .filter((a) => a.performance_ratio < 95)
    .sort((a, b) => a.performance_ratio - b.performance_ratio)
    .slice(0, 5);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Performance & Revenue Analytics</h1>
          <p className="text-slate-500">Asset performance to revenue correlation analysis</p>
        </div>

        {/* Revenue Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200 bg-gradient-to-br from-sky-50 to-white" data-testid="potential-revenue">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Potential Revenue</span>
                <DollarSign className="w-5 h-5 text-sky-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-slate-900">
                {formatCurrency(revenueData?.summary?.potential_revenue || 0, true)}
              </p>
              <p className="text-xs text-slate-500 mt-1">Maximum achievable</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="actual-revenue">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Actual Revenue</span>
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-emerald-600">
                {formatCurrency(revenueData?.summary?.actual_revenue || 0, true)}
              </p>
              <p className="text-xs text-emerald-600 mt-1">
                {formatPercent(
                  ((revenueData?.summary?.actual_revenue || 0) / (revenueData?.summary?.potential_revenue || 1)) * 100
                )}{' '}
                of potential
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-gradient-to-br from-red-50 to-white" data-testid="revenue-gap">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Revenue Gap</span>
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-2xl font-heading font-bold text-red-600">
                -{formatCurrency(revenueData?.summary?.revenue_gap || 0, true)}
              </p>
              <p className="text-xs text-red-600 mt-1">Lost opportunity</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="loss-breakdown">
            <CardContent className="p-5">
              <span className="text-sm font-medium text-slate-600">Loss Breakdown</span>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Downtime</span>
                  <span className="font-mono text-red-600">
                    -{formatCurrency(revenueData?.summary?.downtime_loss || 0, true)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Curtailment</span>
                  <span className="font-mono text-red-600">
                    -{formatCurrency(revenueData?.summary?.curtailment_loss || 0, true)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Performance</span>
                  <span className="font-mono text-red-600">
                    -{formatCurrency(revenueData?.summary?.performance_loss || 0, true)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Waterfall */}
          <Card className="border-slate-200" data-testid="waterfall-chart">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Revenue Waterfall Analysis</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={waterfallData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(v) => `$${Math.abs(v) / 1000000}M`} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value) => formatCurrency(Math.abs(value))}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {waterfallData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.type === 'loss' ? '#ef4444' : entry.name === 'Actual' ? '#10b981' : '#0ea5e9'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-sky-500" />
                  <span className="text-sm text-slate-600">Potential</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-500" />
                  <span className="text-sm text-slate-600">Losses</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span className="text-sm text-slate-600">Actual</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Revenue Trend */}
          <Card className="border-slate-200" data-testid="daily-revenue">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Daily Revenue Trend</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={revenueData?.daily || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `$${v / 1000}k`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} name="Actual" />
                  <Line type="monotone" dataKey="potential" stroke="#0ea5e9" strokeWidth={2} name="Potential" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Performance vs Revenue Scatter + Underperforming Assets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Scatter Chart */}
          <Card className="lg:col-span-2 border-slate-200" data-testid="performance-scatter">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg flex items-center justify-between">
                Performance vs Revenue
                <Badge variant="outline" className="font-normal text-xs">
                  High-value underperformers in red
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="performance"
                    name="Performance"
                    domain={[85, 100]}
                    tickFormatter={(v) => `${v}%`}
                    label={{ value: 'Performance Ratio (%)', position: 'bottom', offset: -5 }}
                  />
                  <YAxis
                    dataKey="revenue"
                    name="Revenue"
                    tickFormatter={(v) => `$${v / 1000}k`}
                    label={{ value: 'Revenue MTD ($)', angle: -90, position: 'insideLeft' }}
                  />
                  <ZAxis dataKey="availability" range={[50, 400]} />
                  <Tooltip
                    formatter={(value, name) =>
                      name === 'Revenue' ? formatCurrency(value) : `${value}%`
                    }
                    labelFormatter={(label) => `Asset: ${label}`}
                  />
                  <Scatter name="Assets" data={scatterData}>
                    {scatterData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.performance < 92 && entry.revenue > 200000
                            ? '#ef4444'
                            : entry.status === 'warning'
                            ? '#f59e0b'
                            : '#0ea5e9'
                        }
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Underperforming Assets */}
          <Card className="border-slate-200" data-testid="underperforming-assets">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Underperforming Assets
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 divide-y divide-slate-100">
              {underperformingAssets.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">All assets performing above threshold</p>
              ) : (
                underperformingAssets.map((asset) => (
                  <div key={asset.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm font-semibold text-slate-900">{asset.asset_id}</p>
                        <p className="text-xs text-slate-500">{asset.site}</p>
                      </div>
                      <div className="text-right">
                        <p
                          className={cn(
                            'text-sm font-semibold',
                            asset.performance_ratio < 90 ? 'text-red-600' : 'text-amber-600'
                          )}
                        >
                          {formatPercent(asset.performance_ratio)}
                        </p>
                        <p className="text-xs text-slate-500">Performance</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Progress
                        value={asset.performance_ratio}
                        className={cn('h-1.5', asset.performance_ratio < 90 && '[&>div]:bg-red-500')}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="text-slate-500">Revenue impact:</span>
                      <span className="text-red-600 font-medium">
                        -{formatCurrency((100 - asset.performance_ratio) * 2500)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Asset Performance Table */}
        <Card className="border-slate-200" data-testid="asset-performance-table">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-heading text-lg">Asset Performance Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-4">Asset</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-4">Site</th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-4">Health</th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-4">Availability</th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-4">Performance</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-4">Revenue MTD</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-4">Gap</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {assets.slice(0, 10).map((asset) => {
                    const potentialRevenue = asset.revenue_mtd / (asset.performance_ratio / 100);
                    const gap = potentialRevenue - asset.revenue_mtd;

                    return (
                      <tr key={asset.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-xs',
                                asset.status === 'operational'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                  : asset.status === 'warning'
                                  ? 'bg-amber-50 text-amber-700 border-amber-300'
                                  : 'bg-red-50 text-red-700 border-red-300'
                              )}
                            >
                              {asset.status}
                            </Badge>
                            <span className="font-mono text-sm font-medium">{asset.asset_id}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-600">{asset.site}</td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Progress value={asset.health_score} className="w-16 h-1.5" />
                            <span className="text-sm font-mono">{asset.health_score}%</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Progress value={asset.availability} className="w-16 h-1.5" />
                            <span className="text-sm font-mono">{formatPercent(asset.availability)}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <span
                            className={cn(
                              'text-sm font-mono font-medium',
                              asset.performance_ratio < 92 ? 'text-red-600' : 'text-slate-900'
                            )}
                          >
                            {formatPercent(asset.performance_ratio)}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-sm font-mono text-emerald-600">{formatCurrency(asset.revenue_mtd, true)}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-sm font-mono text-red-600">-{formatCurrency(gap, true)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
